import { IconUser16 } from '@warp-ds/icons/react';
import { Button, TextField } from '@warp-ds/react';
import cn from 'classnames';
import { type FormEvent, useReducer, useState } from 'react';
import { useConfig } from '../../config/ConfigContext';
import { t } from '../../locale/texts';
import { getOneYearFromToday } from '../../util/datetime-fns';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { hotelAutocompleteMapper } from '../common/autocomplete/utils/AutocompleteMapper';
import { Dropdown } from '../common/Dropdown';
import { FrontPageModal } from '../common/frontPageModal/FrontPageModal';
import { FrontpageDatePickerWrapper } from '../common/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { storeLastSearchHotel } from '../common/lastSearch';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import { trackSearchClick } from '../common/tracking';
import { HotelRooms } from './HotelRooms';
import styles from './HotelSearch.module.css';
import { useHotelInitialState } from './hooks/useHotelInitialState';
import { useHotelValidation } from './hooks/useHotelValidation';
import actions, { reducer } from './hotelActions';
import { hotelDefaultState } from './hotelDefaultState';
import { buildHotelTrackingItem } from './utils/buildHotelTrackingItem';
import { formatRoomInfoText } from './utils/formatRoomInfoText';
import { HotelHiddenInputs } from './utils/HotelHiddenInputs';

type HotelSearchProps = {
    prefill?: Partial<typeof hotelDefaultState>;
    position: string;
};

export const HotelSearch = ({ prefill, position }: HotelSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, { ...hotelDefaultState, ...prefill });

    const { hotelAutocompleteUrl } = useConfig();

    useHotelInitialState(dispatch, prefill);

    const { rooms = [], startDate, endDate, destination, destinationContext } = state;
    const { isValid, invalid } = useHotelValidation(state, pristine);

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        if (isValid) {
            const item = buildHotelTrackingItem({ destinationContext, rooms, startDate, endDate });
            trackSearchClick('Search hotel', '/reise/hotell', 'hotel', `${position} - whitelabel`, item);
            storeLastSearchHotel(state);
        } else {
            setPristine(false);
            event.preventDefault();
        }
    };

    return (
        <SearchPanelWrapper
            className={styles['form-body']}
            onSubmit={handleSubmit}
            action="https://travel-kayak-wl.finn.no/in"
            method="GET"
            dataTestId="hotelSearch">
            <Autocomplete
                vertical="hotel"
                id="destination"
                label={t('hotel.destination.label')}
                placeholder={t('hotel.destination.placeholder')}
                defaultValue={destination}
                onSelect={(option) => dispatch(actions.setDestination(option))}
                url={hotelAutocompleteUrl}
                mapOptions={hotelAutocompleteMapper}
                error={invalid.destination ? t('hotel.validation.destination') : undefined}
            />
            <FrontpageDatePickerWrapper
                upperDateLimit={getOneYearFromToday()}
                startDate={startDate}
                endDate={endDate}
                useRange
                useAnchor={{
                    invalid: invalid.startDate || invalid.endDate || invalid.dateRange,
                    helpText: invalid.startDate || invalid.endDate || invalid.dateRange ? 'Du må velge dato' : undefined,
                }}
                onDateChange={({ startDate, endDate }) => {
                    dispatch(actions.setDates({ startDate, endDate }));
                }}>
                <TextField
                    id="date-range-start"
                    data-testid="Hotel-dateRangeStart"
                    label={t('hotel.checkin.label')}
                    invalid={invalid.startDate || invalid.dateRange}
                    helpText={invalid.startDate && t('hotel.validation.dateRangeStart')}
                />
                <TextField
                    id="date-range-end"
                    data-testid="Hotel-dateRangeEnd"
                    label={t('hotel.checkout.label')}
                    invalid={invalid.endDate || invalid.dateRange}
                    helpText={
                        invalid.endDate
                            ? t('hotel.validation.dateRangeEnd')
                            : invalid.dateRange
                              ? t('hotel.validation.dateRangeGap')
                              : undefined
                    }
                />
            </FrontpageDatePickerWrapper>
            <FrontPageModal
                breakpoint="sm"
                title="Gjester og rom"
                trigger={{
                    text: formatRoomInfoText(rooms),
                    prefix: <IconUser16 aria-hidden />,
                }}
                error={invalid.rooms ? t('hotel.validation.rooms') : undefined}>
                <HotelRooms rooms={rooms} dispatch={dispatch} />
            </FrontPageModal>
            <Dropdown
                trigger={{ text: formatRoomInfoText(rooms), label: 'Reisende' }}
                prefix={<IconUser16 aria-hidden />}
                className="hidden sm:flex">
                <HotelRooms rooms={rooms} dispatch={dispatch} />
            </Dropdown>
            <HotelHiddenInputs destinationContext={destinationContext} startDate={startDate} endDate={endDate} rooms={rooms} />
            <Button
                className={cn(styles['search-button'], {
                    [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                })}
                fullWidth
                type="submit"
                primary
                data-testid="hotelSearchButton">
                {t('action.search')}
            </Button>
        </SearchPanelWrapper>
    );
};
